/**
 * Sweep Control — the health log. Renders get_sweep_health honestly: a
 * needsAttention banner in the sanctioned low-opacity gold register, per-run
 * rows, and an honest empty state. Manual refresh only. A failed background
 * refetch keeps the last-good log (error shown only when nothing ever loaded).
 */

import { useState } from "react";
import type { RawSweepHealth } from "../types";
import { HealthRow } from "./HealthRow";
import { IconRefresh, IconWarning } from "./icons";

export function HealthLog({
  health,
  isPending,
  isError,
  error,
  isFetching,
  onRefresh,
}: {
  health: RawSweepHealth | undefined;
  isPending: boolean;
  isError: boolean;
  error: unknown;
  isFetching: boolean;
  onRefresh: () => void;
}): JSX.Element {
  const runs = health?.runs ?? [];
  const [showAll, setShowAll] = useState(false);
  const visibleRuns = showAll ? runs : runs.slice(0, 5);

  return (
    <section className="sxsw-health">
      <div className="sxsw-health-head">
        <h4 className="sxsw-subhead">Health log</h4>
        <button
          type="button"
          className="sx-btn sx-btn-p3"
          onClick={onRefresh}
          disabled={isFetching}
        >
          <IconRefresh />
          {isFetching ? "Refreshing…" : "Refresh health"}
        </button>
      </div>

      {health?.needsAttention && (
        <div className="sxsw-banner" role="status">
          <span className="sxsw-banner-icon" aria-hidden="true">
            <IconWarning />
          </span>
          <div>
            <p className="sxsw-banner-title">Sweep health needs attention</p>
            {health.missedScheduledWindow && (
              <p className="sxsw-banner-copy">
                No scheduled run started in the most recent scheduled window (
                {health.missedWindowPredicate.timezone}).
              </p>
            )}
          </div>
        </div>
      )}

      {isPending && <p className="sxsw-muted">Loading sweep health…</p>}

      {isError && health === undefined && (
        <p className="sxsw-error">
          {error instanceof Error ? error.message : "Failed to load sweep health."}
        </p>
      )}

      {health !== undefined && runs.length === 0 && (
        <p className="sxsw-muted">No sweep runs recorded yet.</p>
      )}

      {runs.length > 0 && (
        <>
          <ul className="sxsw-runs">
            {visibleRuns.map((run) => (
              <HealthRow key={run.sweepRunId} run={run} />
            ))}
          </ul>
          {runs.length > 5 && (
            <button
              type="button"
              className="sx-btn sx-btn-p3 sxsw-show-runs"
              onClick={() => setShowAll((current) => !current)}
            >
              {showAll ? "Show recent 5" : `Show all ${runs.length} runs`}
            </button>
          )}
        </>
      )}
    </section>
  );
}
